import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./TextArea.module.css";

/**
 * TextArea
 *  type ['text', 'email', 'password', 'number', 'color', 'search', 'tel'] 를 지원함
 * @type React.FC<TextAreaPropTypes>
 */
const TextArea = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(TextAreaPropTypes).includes(p)) {
      console.error(`TextArea: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 값 삭제(초기화) */
    clear() {
      setCurrState((prev) => ({
        ...prev,
        currValue: props.defaultValue ?? "",
        disabled: props.disabled,
        readOnly: props.readOnly,
      }));
    },
    /** 값 가져오기 */
    getValue() {
      return currState.currValue;
    },
    /** 값 지정 */
    setValue(v) {
      setCurrState((prev) => ({
        ...prev,
        currValue: v !== null ? v : "",
      }));
    },
    /** 현재 input에 focus 처리 */
    focus() {
      if (!refInput.current) {
        console.warn("TextArea: refInput 가 아직 준비되지 않았습니다.");
      }
      refInput.current?.focus();
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    setReadOnly: (isReadOnly) => {
      setCurrState((prev) => ({
        ...prev,
        readOnly: isReadOnly,
      }));
    },
    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "TextArea: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  const refInput = useRef();

  /**
   * TextArea Component의 State
   */
  const [currState, setCurrState] = useState({
    disabled: props.disabled,
    readOnly: props.readOnly,
    currValue: props.defaultValue,
    placeholder: props.placeholder,
    minLength: props.minLength,
    maxLength: props.maxLength,
    rows: props.rows,
  });

  /**
   * 변경 이벤트
   */
  const _onChange = (e) => {
    const { value } = e.target;

    setCurrState((prev) => ({
      ...prev,
      currValue: value,
    }));

    if (props.onChange) {
      props.onChange(e);
    }
  };

  /**
   * 키 입력 이벤트
   */
  const _onKeyUp = (e) => {
    if (e.key === "Enter") {
      if (props.onEnterPress) {
        props.onEnterPress(e);
      }
    } else {
      if (props.onKeyUp) {
        props.onKeyUp(e);
      }
    }
  };

  const _onKeyDown = (e) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <textarea
      className={`${styles.e3Textarea} ${props.controlWidth ?? ""} ${
        props.className ?? ""
      }`}
      style={{ resize: props.resize, height: props.height || 100 }}
      ref={refInput}
      disabled={currState.disabled}
      readOnly={currState.readOnly}
      placeholder={currState.placeholder}
      value={currState.currValue}
      rows={currState.rows}
      {...(currState.minLength && { minLength: currState.minLength })}
      {...(currState.maxLength && { maxLength: currState.maxLength })}
      onChange={_onChange}
      onKeyDown={_onKeyDown}
      onKeyUp={_onKeyUp}
    />
  );
});

const TextAreaPropTypes = {
  /**
   * disabled: 비활성화
   * 기본값 false
   * updateState('disabled', bool) 함수로 수정 가능
   */
  disabled: PropTypes.bool,
  /**
   * readOnly: 읽기전용
   * 기본값 false
   * updateState('readOnly', bool) 함수로 수정 가능
   */
  readOnly: PropTypes.bool,

  /**
   * defaultValue: 최초 바인딩 되는 값
   * setValue()를 이용해 value 값 수정 가능
   */
  defaultValue: PropTypes.string,
  /**
   * 값이 비어있을 때 나타나는 내용
   * 기본값 없음
   * updateState('placeholder', string) 함수로 수정 가능
   */
  placeholder: PropTypes.string,
  /**
   * minLength: 텍스트 최소길이
   * 기본값 없음
   * updateState('minLength', number) 함수로 수정 가능
   */
  minLength: PropTypes.number,
  /**
   * maxLength: 텍스트 최대길이
   * 기본값 없음
   * updateState('maxLength', number) 함수로 수정 가능
   */
  maxLength: PropTypes.number,
  /**
   * 변경 이벤트
   */
  onChange: PropTypes.func,
  /**
   * 엔터키 입력 이벤트
   */
  onEnterPress: PropTypes.func,
  /**
   * 키 입력 이벤트
   */
  onKeyUp: PropTypes.func,
  /**
   *
   */
  onKeyDown: PropTypes.func,
  /** */
  controlWidth: PropTypes.string,
  rows: PropTypes.number,
  /**
   * className 적용
   */
  className: PropTypes.string,
  /**
   * resize 적용
   * ['both','horizontal','vertical','none']
   */
  resize: PropTypes.string,
  /**
   * textArea height
   */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TextArea.propTypes = TextAreaPropTypes;
TextArea.defaultProps = {
  disabled: false,
  maxLength: null,
  minLength: null,
  placeholder: "",
  readOnly: false,
  defaultValue: "",
  onChange: null,
  onEnterPress: null,
  onKeyUp: null,
  onKeyDown: null,
  controlWidth: null,
  rows: 5,
  className: "",
  resize: "vertical",
  height: null,
};
TextArea.displayName = "TextArea";
export default TextArea;
